import { useLoyaltyProducts } from "@multicines/hooks";
import { putCancelOrder, useFetchSessionTickets } from "@multicines/services";
import { fetchSessionById } from "@multicines/services";
import { useMoviesStore, useProductsStore } from "@multicines/stores";
import { useShoppingCartStore } from "@multicines/stores";
import { Modality } from "@multicines/types";
import { getShoppingCartProductsByVendor } from "@multicines/utils";
import { getApiMessageError, getTradeCodeApiError } from "@multicines/utils";
import { TICKETS_VENDOR } from "@multicines/utils";
import { getProductTotals } from "artisn/products";
import { getShoppingCartTotal } from "artisn/shopping-cart";
import { Modal } from "artisn-ui-react";
import { doc, onSnapshot } from "firebase/firestore";
import CloseSVG from "images/close.svg";
import EmptyBillboardSVG from "images/empty-billboard.svg";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import React, { useEffect, useMemo } from "react";

import { fetchSelectedMovie } from "./Tickets.helper";
import Styles from "./Tickets.styles";
import { TicketsProps as Props } from "./Tickets.types";
import useTicketAnalytics from "./useTicketAnalytics";
import TicketsCategoryPlaceholder from "../../global/TicketsCategory/TicketsCategoryPlaceholder/TicketsCategoryPlaceholder";
import usePurchaseEvents from "components/checkout/Checkout/hooks/usePurchaseEvents";
import CorporateTicket from "components/corporateTickets/CorporateTicket/CorporateTicket";
import ButtonTotal from "components/global/ButtonTotal/ButtonTotal";
import EmptyState from "components/global/EmptyState/EmptyState";
import ErrorState from "components/global/ErrorState/ErrorState";
import ExitReservation from "components/global/ExitReservation/ExitReservation";
import SelectedMovieDetail from "components/global/SelectedMovieDetail/SelectedMovieDetail";
import Ticket from "components/global/Ticket/Ticket";
import TicketPlaceholder from "components/global/Ticket/TicketPlaceholder/TicketPlaceholder";
import TicketsCategory from "components/global/TicketsCategory/TicketsCategory";
import TicketsHeader from "components/global/TicketsHeader/TicketsHeader";
import { getAuth } from "config/artisn.config";
import { db } from "config/artisn.config";
import { notify } from "config/bugsnag.config";
import CONSTANTS from "config/constants";
import useI18n from "hooks/useI18n";
import usePendingOrder from "hooks/usePendingOrder";
import useShoppingCart from "hooks/useShoppingCart/useShoppingCart";
import useTalkShop from "hooks/useTalkShop";
import useWarningOnExit from "hooks/useWarningOnExit";
import { useAuthStore } from "stores/auth/auth.store";
import { useCheckoutStore } from "stores/checkout/checkout.store";
import { useStoresStore } from "stores/stores/stores.store";
import { createErrorNotification } from "utils/notifications.utils";
import { dismissErrorNotification } from "utils/notifications.utils";
const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;

const Tickets: React.FC<Props> = props => {
  const { back } = useRouter();
  const currentUser = getAuth.currentUser;
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const loyaltyBenefits = useLoyaltyProducts({ isAnonymous, uid });
  const { isTalkShop, navigateWithTalkShop } = useTalkShop();
  const selectedStore = useStoresStore(store => store.selectedStore);
  const fetchSessionTickets = useFetchSessionTickets(
    { isAnonymous, uid },
    selectedStore
  );
  const t = useI18n();
  const { logPurchaseFail } = usePurchaseEvents();
  const selectedSession = useMoviesStore(store => store.selectedSession);
  const isSessionExpired = useMoviesStore(store => store.isSessionExpired);
  const setReservationExpiration = useMoviesStore(
    store => store.setReservationExpiration
  );
  const resetSeatsToAllocate = useMoviesStore(
    store => store.resetSeatsToAllocate
  );
  const ticketsCount = useMoviesStore(store => store.ticketsCount);
  const setTicketsCount = useMoviesStore(store => store.setTicketsCount);
  const setSelectedMovie = useMoviesStore(store => store.setSelectedMovie);
  const selectedMovie = useMoviesStore(store => store.selectedMovie);
  const setSelectedSession = useMoviesStore(store => store.setSelectedSession);
  const resetMovies = useMoviesStore(store => store.resetMovies);
  const mustUsePoints = useMoviesStore(store => store.mustUsePoints);
  const shoppingCart = useShoppingCartStore(store => store.shoppingCart);
  const step = useCheckoutStore(state => state.step);
  const placedOrder = useCheckoutStore(state => state.placedOrder);
  const setSnackRestrictionCard = useProductsStore(
    store => store.setSnackRestrictionCard
  );
  const { validatePendingOrder } = usePendingOrder();
  const shoppingCartHook = useShoppingCart();
  const addSeatsToAllocate = useMoviesStore(store => store.addSeatsToAllocate);
  const { logAddProductToCartEvent } = useTicketAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [opened, setOpened] = useState(false);
  const [isContinue, setIsContinue] = useState(false);
  const [isAddingCart, setIsAddingCart] = useState(false);
  const [showCorporateInput, setShowCorporateInput] = useState(false);

  const { availableBenefitTickets, points } = loyaltyBenefits;

  const [remainingPoints, setRemainingPoints] = useState(points);

  const { data: sessionTickets, isFetching } = fetchSessionTickets;
  const { status, error, refetch } = fetchSessionTickets;
  const { areas: sessionCategories } = sessionTickets ?? {};
  const { quantityAvailablePerOrder = 999 } = sessionTickets ?? {};

  const [ticketsStore] = selectedStore ?? [];
  const { sessionId } = selectedSession ?? {};
  console.log("sessionId:", sessionId);

  console.log(selectedMovie?.externalId);

  const isResolved = step === "RESOLVED";

  const { max } = t.errors.tickets;

  const shoppingCartTotals = useMemo(() => {
    if (!shoppingCart) return;
    return getShoppingCartTotal(shoppingCart);
  }, [shoppingCart]);

  const { deleteShoppingCart } = shoppingCartHook;

  const { vendor } = ticketsStore ?? {};
  const { id: selectedTicketVendorId } = vendor ?? {};

  const tickets = useMemo(
    () =>
      getShoppingCartProductsByVendor(
        shoppingCart,
        selectedTicketVendorId ?? TICKETS_VENDOR.id
      ),
    [selectedTicketVendorId, shoppingCart]
  );
  const ticketsInCart = tickets?.length;

  const changeHandler = useCallback(
    (amount: number, last: number) => {
      dismissErrorNotification();
      setTicketsCount(prev => prev + (amount - last));
    },
    [setTicketsCount]
  );

  const refetchSessionTicketsQuery = async () => {
    await refetch();
  };

  const continueHandler = async () => {
    setIsContinue(true);
    setIsLoading(true);

    if (!ticketsInCart) {
      createErrorNotification(
        t.tickets.minError,
        undefined,
        dismissErrorNotification
      );
      setIsLoading(true);
      return;
    }

    const onContinue = () => {
      processTickets();
      navigateWithTalkShop("/seats");
      setIsLoading(false);
    };

    const onError = (error: string) => {
      createErrorNotification(
        error,
        undefined,
        dismissErrorNotification,
        1000 * 30
      );
      setIsContinue(false);
      setIsLoading(false);
    };

    try {
      await validatePendingOrder(onContinue, onError);
    } catch (e) {
      notify(error, "continueHandler - Tickets");
      setIsContinue(false);
      setIsLoading(false);
      const message = getApiMessageError(e);
      createErrorNotification(message, undefined, dismissErrorNotification);
    }
  };

  const processTickets = () => {
    tickets?.forEach(ticket => {
      // @ts-ignore
      const { attributes, ticketsQuantity = 1, amount = 1 } = ticket ?? {};
      const { ticketCategory } = attributes ?? {};
      if (!ticketCategory) {
        throw new Error("Ticket category not found");
      }
      const totalSeats = ticketsQuantity * amount;
      addSeatsToAllocate(ticketCategory, totalSeats, true);

      logAddProductToCartEvent(ticket);
    });
  };

  const dismissHandler = () => {
    setOpened(true);
  };

  const closeHandler = () => {
    setOpened(false);
  };

  const disableCounters =
    ticketsCount >= quantityAvailablePerOrder || isFetching || isAddingCart;
  const isLoadingButtonTotal = isLoading || isFetching;
  const isDisabledButtonTotal =
    isLoadingButtonTotal || !ticketsInCart || isAddingCart;

  useEffect(() => {
    const disabledCounter = ticketsCount >= quantityAvailablePerOrder;
    if (disabledCounter) createErrorNotification(max);
  }, [ticketsCount, quantityAvailablePerOrder, max]);

  useEffect(() => {
    if (!shoppingCartTotals || !ticketsInCart || !mustUsePoints) {
      setRemainingPoints(points);
      return;
    }
    const { totalPoints } = shoppingCartTotals;
    setRemainingPoints(points - (totalPoints ?? 0));
  }, [mustUsePoints, points, shoppingCartTotals, ticketsInCart]);

  useWarningOnExit(!opened && !!selectedSession && !isContinue, t, async () => {
    if (!ticketsInCart || isResolved || isSessionExpired) return;

    try {
      const deletedShoppingCart = await deleteShoppingCart?.({
        shoppingCartName: SHOPPING_CART_DEFAULT_NAME
      });
      if (deletedShoppingCart && placedOrder) {
        logPurchaseFail(t.errors.purchaseFail, deletedShoppingCart);
        await putCancelOrder(placedOrder.id);
      }
      // Refresh the token
      getAuth.currentUser?.getIdToken(true);
      resetSeatsToAllocate();
      setReservationExpiration(undefined);
      resetMovies();
      setSnackRestrictionCard(undefined);
    } catch (e) {
      notify(error, "deletedShoppingCart - Tickets");
      console.warn(`${t.movie.cancelReservationError} ${e.message}`);
    }
  });

  useEffect(() => {
    if (!error) return;
    createErrorNotification(error.message, undefined, dismissErrorNotification);
  }, [error]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, `appParameters/${ACCOUNT_ID}`),
      doc => {
        if (doc.exists()) {
          const appParameters = doc.data();
          if (!appParameters) return;
          const { featureFlags } = appParameters;
          const { withCorporativeTicketWeb } = featureFlags ?? {};
          setShowCorporateInput(!!withCorporativeTicketWeb);
        }
      }
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    (async () => {
      if (!window) return;
      const url = new URL(window.location.href);
      const paramsSessionId = url.searchParams.get("sessionId") ?? undefined;
      if (!paramsSessionId) return;
      const paramsModality = (url.searchParams.get("modality") ??
        "All") as Modality; // Get modality from somewhere
      const paramsMovieId = url.searchParams.get("movieId") ?? undefined;
      if (!paramsMovieId) return;
      const [ticketsStore] = selectedStore ?? [];
      const { vendor } = ticketsStore ?? {};
      const { id: vendorId } = vendor ?? {};
      const { externalId } = ticketsStore?.additionalInfo ?? {};
      const token = await currentUser?.getIdToken();
      if (
        !paramsMovieId ||
        !token ||
        !externalId ||
        !vendorId ||
        !paramsSessionId
      ) {
        return;
      }
      // Fetch selectedSession and selectedMovie
      try {
        const selectMovie = await fetchSelectedMovie(
          externalId!,
          paramsMovieId,
          paramsModality,
          vendorId!
        );
        setSelectedMovie(selectMovie);
        const selectSession = await fetchSessionById(
          paramsMovieId,
          externalId,
          vendorId,
          paramsSessionId
        );
        setSelectedSession(selectSession);
      } catch (error) {
        notify(error, "fetchSelectedMovie - Tickets");
        createErrorNotification(
          "",
          t.errors.tickets.deeplink,
          dismissErrorNotification
        );
      }
    })();
  }, [
    currentUser,
    selectedStore,
    setSelectedMovie,
    setSelectedSession,
    t.errors.tickets.deeplink
  ]);

  return (
    <>
      <Styles className="Tickets">
        <header className="Tickets__header">
          <TicketsHeader
            text={t.tickets.selectTickets}
            showTimer={false}
            step="choose-tickets"
            canClose={!isTalkShop}
            onClose={dismissHandler}
            onGoBack={dismissHandler}
          />
        </header>
        <main className="Tickets__main">
          <SelectedMovieDetail className="Tickets__mobile" />
          <div className="Tickets__container">
            {showCorporateInput ? <CorporateTicket /> : null}
            {/* TODO: this is mock */}
            {/* <p
              style={{
                fontSize: "16px",
                margin: "0 0 10px 0",
                fontWeight: "500",
                color: "#b2a6a6"
              }}
            >
              Total Por Orden: {quantityAvailablePerOrder ?? 0}
            </p> */}

            {status === "error" ? (
              <ErrorState
                title={getTradeCodeApiError(error, "TMT", true)}
                description={getApiMessageError(error)}
                onClick={refetchSessionTicketsQuery}
              />
            ) : null}
            {status === "loading" || status === "idle" ? (
              <TicketsCategoryPlaceholder />
            ) : null}
            {status === "success" && !sessionCategories?.length ? (
              <EmptyState
                icon={<EmptyBillboardSVG />}
                title={t.tickets.empty}
                description={t.common.emptyMessage}
                buttonTitle={t.common.changeSession}
                onClick={() => back()}
              />
            ) : null}
            {status === "success" && !!sessionCategories?.length ? (
              <>
                {sessionCategories?.map((category, index) => {
                  return (
                    <TicketsCategory
                      key={index}
                      category={category}
                      onChange={changeHandler}
                      disabled={disableCounters}
                      remainingPoints={remainingPoints}
                      onIsAdding={setIsAddingCart}
                      availableBenefitTickets={availableBenefitTickets}
                    />
                  );
                })}
              </>
            ) : null}
          </div>
          <div className="Tickets__movies">
            <div className="Tickets__desktop">
              <SelectedMovieDetail className="Tickets__selected" />
            </div>
            <p className="Tickets__movies__title">{t.tickets.title}</p>
            {status === "success" && !isAddingCart ? (
              <>
                <div className="Tickets__movies__list">
                  {tickets?.map((ticket, index) => {
                    const { amount, name, prices } = ticket;
                    const { POINTS } = prices;
                    const { netPrice } =
                      getProductTotals(ticket, amount ?? 1) ?? {};
                    return (
                      <Ticket
                        key={index}
                        className="Tickets__ticket"
                        type={amount?.toString()}
                        name={name}
                        price={netPrice}
                        points={POINTS?.netPrice}
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              <TicketPlaceholder />
            )}
            <ButtonTotal
              className="Tickets__button-desktop"
              onClick={continueHandler}
              vendor="tickets"
              isLoading={isLoadingButtonTotal}
              disabled={isDisabledButtonTotal}
            />
          </div>
          <div className="Tickets__sticky-button">
            <ButtonTotal
              className="Tickets__button-mobile"
              onClick={continueHandler}
              vendor="tickets"
              isLoading={isLoadingButtonTotal}
              disabled={isDisabledButtonTotal}
            />
          </div>
        </main>
      </Styles>

      <Modal
        {...props}
        closeOnClickOutside
        opened={opened}
        backdropConfig={{
          onClick: closeHandler
        }}
        onClose={closeHandler}
        closeIcon={<CloseSVG />}
        color="transparent"
      >
        <ExitReservation shouldCancel={false} onClose={closeHandler} />
      </Modal>
    </>
  );
};

export default Tickets;
